import type { ClientRelease } from '../../shared/types/collection'
import { createInfiniteList } from '../utils/infiniteList'
import {
	initReleaseListItem,
	setReleaseListItemDimState,
	useLargeReleaseListCover,
} from './ReleaseListItem'
import { releaseListGroupHeaderTemplate } from './releaseListTemplates'

type FetchFn = (
	offset: number,
	limit: number,
) => Promise<{ items: ClientRelease[] } | null>

type ReleaseListLoader = ReturnType<typeof createInfiniteList<ClientRelease>>

export type ReleaseListGroupInput = {
	key: string
	label: string
	sideIndexLabel?: string
}

export type ReleaseListGroup = ReleaseListGroupInput & {
	id: string
}

type ReleaseListInitOptions = {
	onFetchingChange?: (isFetching: boolean, offset: number) => void
	containerEl: HTMLElement
	initialItems: ClientRelease[]
	hasMore?: boolean
	fetchFunction: FetchFn
	limit: number
	dimReleases?: boolean
	groupBy?: (release: ClientRelease) => ReleaseListGroupInput
	groupIdPrefix?: string
	onGroupsChange?: (groups: ReleaseListGroup[]) => void
	/** Whether items should load full covers instead of thumbnails. */
	largeCovers?: () => boolean
}

const DEFAULT_GROUP_ID_PREFIX = 'release-list-group'

class ReleaseListController {
	ready: Promise<void> = Promise.resolve()
	private currentDimReleases: boolean
	private lastGroupKey: string | null = null
	private readonly renderedGroups = new Map<string, ReleaseListGroup>()
	private readonly groupOccurrences = new Map<string, number>()
	private readonly containerEl: HTMLElement
	private readonly fetchFunction: FetchFn
	private readonly groupBy?: (release: ClientRelease) => ReleaseListGroupInput
	private readonly groupIdPrefix: string
	private readonly onGroupsChange?: (groups: ReleaseListGroup[]) => void
	private readonly largeCovers?: () => boolean
	private readonly loader: ReleaseListLoader

	constructor({
		containerEl,
		initialItems,
		hasMore,
		fetchFunction,
		limit,
		onFetchingChange,
		dimReleases = true,
		groupBy,
		groupIdPrefix = DEFAULT_GROUP_ID_PREFIX,
		onGroupsChange,
		largeCovers,
	}: ReleaseListInitOptions) {
		this.containerEl = containerEl
		this.fetchFunction = fetchFunction
		this.currentDimReleases = dimReleases
		this.groupBy = groupBy
		this.groupIdPrefix = groupIdPrefix
		this.onGroupsChange = onGroupsChange
		this.largeCovers = largeCovers

		this.renderItem = this.renderItem.bind(this)
		this.loader = createInfiniteList({
			containerEl: () => this.containerEl,
			initialItems,
			initialHasMore: hasMore,
			fetchFunction,
			onFetchingChange,
			limit,
			renderItem: this.renderItem,
		})

		this.init()
	}
	private init(): void {
		this.ready = this.loader.start()
	}
	private renderItem(
		release: ClientRelease,
		index: number,
	): HTMLElement | HTMLElement[] {
		const releaseItem = initReleaseListItem(
			release,
			index,
			this.currentDimReleases,
		)
		if (this.largeCovers?.()) useLargeReleaseListCover(releaseItem)
		const group = this.groupBy?.(release)
		if (!group) return releaseItem

		const groupHeader = this.renderGroupHeaderIfNeeded(group)
		return groupHeader ? [groupHeader, releaseItem] : releaseItem
	}

	private renderGroupHeaderIfNeeded(
		group: ReleaseListGroupInput,
	): HTMLLIElement | null {
		if (group.key === this.lastGroupKey) return null

		this.lastGroupKey = group.key
		const count = (this.groupOccurrences.get(group.key) ?? 0) + 1
		this.groupOccurrences.set(group.key, count)

		const firstGroupId =
			this.renderedGroups.get(group.key)?.id ??
			`${this.groupIdPrefix}-${toGroupIdPart(group.key)}`
		const headerId = count === 1 ? firstGroupId : `${firstGroupId}-${count}`

		if (!this.renderedGroups.has(group.key)) {
			this.renderedGroups.set(group.key, { ...group, id: firstGroupId })
			this.notifyGroupsChange()
		}

		return releaseListGroupHeaderTemplate(group, headerId)
	}
	reload(
		items: ClientRelease[],
		newFetchFn = this.fetchFunction,
		hasMore?: boolean,
	): void {
		this.resetGroups()
		this.loader.reset({
			initialItems: items,
			fetchFunction: newFetchFn,
			hasMore,
		})
		this.ready = this.loader.start()
	}

	pause(): void {
		this.loader.pause()
	}
	cancelPending(): void {
		this.loader.cancelPending()
	}
	resume(): void {
		this.loader.resume()
	}
	get count(): number {
		return this.loader.count
	}

	updateDimState(dimReleases: boolean): void {
		this.currentDimReleases = dimReleases

		this.containerEl
			.querySelectorAll<HTMLLIElement>('.release')
			.forEach((item) => {
				setReleaseListItemDimState(
					item,
					item.dataset.isHidden === 'true' && dimReleases,
				)
			})
	}

	destroy(): void {
		this.loader.destroy()
	}

	private resetGroups(): void {
		this.lastGroupKey = null
		this.renderedGroups.clear()
		this.groupOccurrences.clear()
		this.notifyGroupsChange()
	}

	private notifyGroupsChange(): void {
		this.onGroupsChange?.(Array.from(this.renderedGroups.values()))
	}
}

export function initReleaseList(
	options: ReleaseListInitOptions,
): ReleaseListController {
	return new ReleaseListController(options)
}

function toGroupIdPart(key: string): string {
	const normalized = key
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
	return normalized || 'fallback'
}
